import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import './TimeLine.css';

interface Phase {
  name: string;
  order: number;
  items: string[];
}

interface TimeLineProps {
  data: {
    title?: string;
    phases: Phase[];
    considerarOrdem?: boolean;
  };
}

const TimeLine: React.FC<TimeLineProps> = ({ data }) => {
  const [phases, setPhases] = useState<Phase[]>([]);
  const [result, setResult] = useState('');

  const shuffle = useCallback((array: any[]) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }, []);

  useEffect(() => {
    if (data) {
      setPhases(shuffle([...data.phases]));
    }
  }, [data, shuffle]);

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
      return;
    }

    const reorderedPhases = Array.from(phases);
    const [movedPhase] = reorderedPhases.splice(source.index, 1);
    reorderedPhases.splice(destination.index, 0, movedPhase);

    setPhases(reorderedPhases);
  };

  useEffect(() => {
    if (phases.length > 0) {
      initializePhases();
    }
  }, [phases]);

  const initializePhases = useCallback(() => {
    phases.forEach(phase => {
      const editableDiv = document.querySelector<HTMLDivElement>(`[data-phase="${phase.name}"]`);
      if (editableDiv) {
        // Remove os event listeners existentes antes de adicionar novos
        console.log(`Listeners added to phase: ${phase.name}`);
      }
    });
  }, [phases]);

  const handleClick = (e: MouseEvent) => {
    const div = e.currentTarget as HTMLDivElement;
    const spans = div.querySelectorAll('.timeline-item-label');
    const lastSpan = spans[spans.length - 1];
    if (lastSpan) {
      const range = document.createRange();
      range.setStartAfter(lastSpan);
      range.setEndAfter(lastSpan);
      const br = document.createElement('br');
      range.insertNode(br);
      range.setStartAfter(br);
      range.setEndAfter(br);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  const tokenize = (content: string): string[] => {
    // Remove acentos, normaliza espaços e converte para minúsculas
    const normalizeText = (text: string) => {
      return text
        .normalize('NFD') // Decompor caracteres acentuados
        .replace(/[\u0300-\u036f]/g, '') // Remover marcas de acentuação
        .trim() // Remover espaços em branco nas pontas
        .toLowerCase(); // Converter para minúsculas
    };
  
    // Extrai o primeiro item fora das divs e depois divide pelos divs
    const firstItemMatch = content.match(/^(.*?)<div[^>]*>/i);
    const firstItem = firstItemMatch ? firstItemMatch[1] : '';
    const divItems = content.split(/<\/div><div[^>]*>|<div[^>]*>|<\/div>/gi);
  
    // Adiciona o primeiro item no início, se existir
    if (firstItem.trim()) {
      divItems.unshift(firstItem);
    }
  
    // Normaliza e filtra itens vazios
    return divItems
      .map(item => normalizeText(item))
      .filter(Boolean);
  };
  
  const tokenizeComparison = (content: string): string[] => {
    // Remove acentos, normaliza espaços e converte para minúsculas
    const normalizeText = (text: string) => {
      return text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
    };
  
    // Divide os spans dentro de uma div de comparação
    const spanItems = content.split(/<span[^>]*>|<\/span>/gi);
  
    return spanItems
      .map(item => normalizeText(item))
      .filter(Boolean);
  };
  
  
  const checkOrderWithoutConsiderarOrdem = () => {
    const phasesContainer = document.getElementById("timeline-phases-container");
    const phasesList = phasesContainer?.querySelectorAll<HTMLLIElement>(".timeline-phase-column > li");
    let correctPhaseCount = 0;
    let correctItemsCount = 0;
    let totalItemsCount = 0;
  
    // Verifica se as fases estão na ordem correta
    phasesList?.forEach((item, index) => {
      if (parseInt(item.dataset.order!) === index + 1) {
        item.classList.add("timeline-correct");
        item.classList.remove("timeline-incorrect");
        correctPhaseCount++;
      } else {
        item.classList.add("timeline-incorrect");
        item.classList.remove("timeline-correct");
      }
    });
  
    const inputs = document.querySelectorAll<HTMLDivElement>('.timeline-item-input');
    inputs.forEach((input) => {
      const correctAnswers = new Set(tokenizeComparison(input.dataset.correctAnswer!));
      const userAnswers = new Set(
        tokenize(input.innerHTML)
      );
  
      totalItemsCount += correctAnswers.size;
      let newValue = '';
      let correctInThisPhase = 0;
  
      userAnswers.forEach(answer => {
        if (correctAnswers.has(answer)) {
          newValue += `<div><span class="timeline-item-label" style="color:green; background-color:white">${answer}</span></div>`;
          correctItemsCount++;
          correctInThisPhase++;
          correctAnswers.delete(answer);
        } else {
          newValue += `<div><span class="timeline-item-label" style="color:red; text-decoration: line-through; background-color:white">${answer}</span></div>`;
        }
      });
  
      correctAnswers.forEach(answer => {
        newValue += `<div><span class="timeline-item-label" style="color:orange; background-color:white">${answer}</span></div>`;
      });
  
      input.innerHTML = newValue.trim();
      input.style.height = 'auto';
      input.style.height = input.scrollHeight + 'px';
  
      if (correctInThisPhase === userAnswers.size && correctAnswers.size === 0) {
        input.classList.add('timeline-correct');
        input.classList.remove('timeline-incorrect', 'timeline-partial');
      } else if (correctInThisPhase > 0) {
        input.classList.add('timeline-partial');
        input.classList.remove('timeline-correct', 'timeline-incorrect');
      } else {
        input.classList.add('timeline-incorrect');
        input.classList.remove('timeline-correct', 'timeline-partial');
      }
    });
  
    const phaseScore = (correctPhaseCount / phasesList!.length) * 100;
    const itemsScore = (correctItemsCount / totalItemsCount) * 100;
  
    setResult(`Fases corretas: ${phaseScore.toFixed(2)}%. Itens corretos: ${itemsScore.toFixed(2)}%`);
  };
  

  const checkOrderWithConsiderarOrdem = () => {
    const phasesContainer = document.getElementById("timeline-phases-container");
    const phasesList = phasesContainer?.querySelectorAll<HTMLLIElement>(".timeline-phase-column > li");
    let correctPhaseCount = 0;
    let correctItemsCount = 0;
    let totalItemsCount = 0;
  
    // Verifica se as fases estão na ordem correta
    phasesList?.forEach((item, index) => {
      if (parseInt(item.dataset.order!) === index + 1) {
        item.classList.add("timeline-correct");
        item.classList.remove("timeline-incorrect");
        correctPhaseCount++;
      } else {
        item.classList.add("timeline-incorrect");
        item.classList.remove("timeline-correct");
      }
    });
  
    const inputs = document.querySelectorAll<HTMLDivElement>('.timeline-item-input');
    inputs.forEach((input) => {
      const correctAnswers = tokenizeComparison(input.dataset.correctAnswer!);
      const userAnswers = tokenizeUserInput(input.innerHTML);
  
      totalItemsCount += correctAnswers.length;
      let newValue = '';
      let correctInThisPhase = 0;
  
      correctAnswers.forEach((answer, index) => {
        if (userAnswers[index] === answer) {
          newValue += `<div><span class="timeline-item-label" style="color:green; background-color:white">${answer}</span></div>`;
          correctItemsCount++;
          correctInThisPhase++;
        } else if (userAnswers.includes(answer)) {
          newValue += `<div><span class="timeline-item-label" style="color:blue; background-color:white">${userAnswers[userAnswers.indexOf(answer)]}</span></div>`;
        } else {
          newValue += `<div><span class="timeline-item-label" style="color:orange; background-color:white">${answer}</span></div>`;
        }
      });
  
      userAnswers.forEach((answer) => {
        if (!correctAnswers.includes(answer)) {
          newValue += `<div><span class="timeline-item-label" style="color:red; text-decoration: line-through; background-color:white">${answer}</span></div>`;
        }
      });
  
      input.innerHTML = newValue.trim();
      input.style.height = 'auto';
      input.style.height = input.scrollHeight + 'px';
  
      if (correctInThisPhase === correctAnswers.length) {
        input.classList.add('timeline-correct');
        input.classList.remove('timeline-incorrect', 'timeline-partial');
      } else if (correctInThisPhase > 0) {
        input.classList.add('timeline-partial');
        input.classList.remove('timeline-correct', 'timeline-incorrect');
      } else {
        input.classList.add('timeline-incorrect');
        input.classList.remove('timeline-correct', 'timeline-partial');
      }
    });
  
    const phaseScore = (correctPhaseCount / phasesList!.length) * 100;
    const itemsScore = (correctItemsCount / totalItemsCount) * 100;
  
    setResult(`Fases corretas: ${phaseScore.toFixed(2)}%. Itens corretos: ${itemsScore.toFixed(2)}%`);
  };
  
  const tokenizeComparisonWith = (content: string): string[] => {
    // Tokeniza o conteúdo correto (contém spans)
    return content
      .replace(/<span[^>]*>/g, '') // Remove tags de abertura de span
      .replace(/<\/span>/g, '') // Remove tags de fechamento de span
      .split(/\s+/) // Separa os itens por espaços
      .map(item => item.trim().toLowerCase())
      .filter(Boolean); // Remove itens vazios
  };
  
  const tokenizeUserInput = (content: string): string[] => {
    // Tokeniza o conteúdo do usuário (contém divs)
    const divs = content.split(/<\/div><div[^>]*>|<div[^>]*>|<\/div>/gi);
    const firstItem = divs.shift()?.trim().toLowerCase(); // Considera o primeiro item que não está em uma div
    if (firstItem) {
      return [firstItem, ...divs.map(div => div.trim().toLowerCase()).filter(Boolean)];
    } else {
      return divs.map(div => div.trim().toLowerCase()).filter(Boolean);
    }
  };
  

  const checkOrder = () => {
    if (data.considerarOrdem) {
        checkOrderWithConsiderarOrdem();
    } else {
        checkOrderWithoutConsiderarOrdem();
    }
  };

  const clearIncorrects = () => {
    const inputs = document.querySelectorAll<HTMLDivElement>('.timeline-item-input');
    inputs.forEach(input => {
      const spans = input.querySelectorAll<HTMLSpanElement>('span.timeline-item-label');
      let newValue = '';
      let firstItemProcessed = false;
  
      spans.forEach(span => {
        if (span.style.color === 'green') {
          // Mantém a formatação original do primeiro item
          if (!firstItemProcessed) {
            newValue += `${span.innerText}`;
            firstItemProcessed = true;
          } else {
            newValue += `<div>${span.innerText}</div>`;
          }
        }
      });
  
      input.innerHTML = newValue.trim();
      input.style.height = 'auto';
      input.style.height = input.scrollHeight + 'px';
  
      if (newValue.trim() === '') {
        input.classList.remove('timeline-incorrect', 'timeline-partial', 'timeline-correct');
      } else if (input.querySelectorAll('div').length === spans.length) {
        input.classList.add('timeline-correct');
      } else {
        input.classList.add('timeline-partial');
      }
    });
  };
  
  const clearFormat = () => {
    const inputs = document.querySelectorAll<HTMLDivElement>('.timeline-item-input');
    inputs.forEach(input => {
      const spans = input.querySelectorAll<HTMLSpanElement>('span');
      let newValue = '';
      spans.forEach(span => {
        newValue += `<span class="timeline-item-label">${span.innerText}</span><br>`;
      });
      input.innerHTML = newValue.trim();
      input.style.height = 'auto';
      input.style.height = input.scrollHeight + 'px';
      input.style.backgroundColor = 'white';
      if (newValue.trim() === '') {
        input.classList.remove('timeline-incorrect', 'timeline-partial');
      }
    });
  };

  const showAnswers = () => {
    const inputs = document.querySelectorAll<HTMLDivElement>('.timeline-item-input');
    inputs.forEach(input => {
      input.innerHTML = input.dataset.correctAnswer!
        .replace(/<span class="timeline-item-label">/g, '<span class="timeline-item-label">')
        .replace(/<\/span>/g, '</span><br>');
      input.style.height = 'auto';
      input.style.height = input.scrollHeight + 'px';
    });
  };

const clearAll = () => {
  // Limpa os conteúdos e classes das fases
  const inputs = document.querySelectorAll<HTMLDivElement>('.timeline-item-input');
  inputs.forEach(input => {
    input.innerHTML = '';
    input.classList.remove('timeline-correct', 'timeline-incorrect', 'timeline-partial');
    input.style.height = 'auto';
  });

  // Remove as classes de estilo das fases
  const phasesList = document.querySelectorAll<HTMLLIElement>('.timeline-phase-column > li');
  phasesList.forEach(phase => {
    phase.classList.remove('timeline-correct', 'timeline-incorrect');
  });

  // Reseta o resultado e embaralha as fases novamente
  setResult('');
  setPhases(shuffle([...data.phases]));
};


  return (
    <div className="timeline-game">
      {data.title && <h3>{data.title}</h3>}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="phases" direction="horizontal">
          {(provided) => (
            <div id="timeline-phases-container" className="timeline-phases-container" ref={provided.innerRef} {...provided.droppableProps}>
              {phases.map((phase, index) => (
                <Draggable key={phase.name} draggableId={`phase-${phase.name}`} index={index}>
                  {(provided) => (
                    <div
                      className="timeline-phase-column"
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <li data-order={phase.order} data-name={phase.name}>{phase.name}</li>
                      <div
                        className="timeline-item-input"
                        data-phase={phase.name}
                        data-correct-answer={phase.items.map(item => `<span class="timeline-item-label">${item}</span>`).join('')}
                        contentEditable="true"
                        onClick={handleClick as any}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <div className="actions">
        <button className="timeline-button" onClick={checkOrder}>Avaliar</button>
        <button className="timeline-button" onClick={clearIncorrects}>Limpar Incorretos</button>
        <button className="timeline-button" onClick={showAnswers}>Mostrar Itens</button>
        <button className="timeline-button" onClick={clearAll}>Reiniciar</button>
      </div>
      <div className="timeline-result">{result}</div>
    </div>
  );
};

export default TimeLine;
